/**
 * CNPJ normalization and check-digit validation (IN RFB 2.229/2024).
 *
 * From July 2026 the first 12 positions may be alphanumeric; the two check
 * digits stay numeric. The module-11 weights are unchanged — each character
 * contributes `charCodeAt(i) - 48` (so `'0'`…`'9'` keep their digit value and
 * `'A'`…`'Z'` become 17…42).
 */
const CNPJ_LENGTH = 14;
/** Uppercase, punctuation stripped, keeping only `[0-9A-Z]`. */
export function normalizeCnpj(value) {
    if (value == null)
        return '';
    return value.toUpperCase().replace(/[^0-9A-Z]/g, '');
}
/**
 * Value of one CNPJ character for the module-11 sum (ASCII − 48).
 * Digits keep 0–9; letters become 17–42.
 */
function cnpjCharValue(char) {
    return char.charCodeAt(0) - 48;
}
/**
 * One check digit over `base` (12 chars for the first DV, 13 for the second).
 * Weights 2…9 right-to-left, restarting after the 8th character.
 */
function cnpjCheckDigit(base) {
    let sum = 0;
    for (let i = 0; i < base.length; i++) {
        const weight = (i % 8) + 2;
        sum += cnpjCharValue(base[base.length - 1 - i]) * weight;
    }
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
}
/**
 * True when `value` is a 14-character CNPJ (numeric or alphanumeric) whose
 * check digits match. Empty / half-typed input is false — emptiness is owned
 * by `required` / the form, not by this predicate.
 */
export function isValidCnpj(value) {
    const normalized = normalizeCnpj(value);
    if (normalized.length !== CNPJ_LENGTH)
        return false;
    // First 12 may be alphanumeric; the two DVs are always digits.
    if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(normalized))
        return false;
    const body = normalized.slice(0, 12);
    const d1 = cnpjCheckDigit(body);
    const d2 = cnpjCheckDigit(body + String(d1));
    return normalized.slice(12) === `${d1}${d2}`;
}
//# sourceMappingURL=cnpj.js.map